import BackButton from "../components/BackButton";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { api } from "../lib/api";

/**
 * Standalone /verify-email — handles the case where the user opens the email
 * link but the account has already been verified. Prevents the confusing
 * "Codice non valido" when the OTP purpose doesn't match.
 */
export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const initialEmail = sp.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState("unknown"); // unknown | verified | pending
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!email) return;
    api.publicConfig().catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verification-status?email=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then((d) => setStatus(d.verified ? "verified" : "pending"))
      .catch(() => setStatus("pending"));
  }, [email]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await api.verifyEmail(email.trim(), otp);
      if (res.already_verified) {
        toast.success("Account già verificato. Ti porto al login.");
        setTimeout(() => nav("/login"), 800);
        return;
      }
      toast.success("Email verificata. Ora accedi.");
      nav("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Codice non valido o scaduto";
      toast.error(typeof detail === "string" ? detail : "Codice non valido o scaduto");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.resendOtp(email.trim(), "register_verify"); toast.success("Nuovo codice inviato."); }
    catch { toast.error("Errore nell'invio"); }
  };

  return (
    <div className="min-h-dvh pxos-gradient-surface grid place-items-center px-4" data-testid="verify-email-page">
      <BackButton to="/login" testid="verify-email-back-btn" />
      <div className="w-full max-w-md pxos-card p-8">
        <h1 className="font-display text-2xl font-semibold text-center flex items-center justify-center gap-2">
          <Mail size={20} /> Verifica email
        </h1>

        {status === "unknown" && !email && (
          <form onSubmit={(e) => { e.preventDefault(); }} className="mt-6 space-y-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest">La tua email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     className="pxos-input" placeholder="nome@dominio.com" data-testid="verify-email-input" />
            </label>
            <button type="button" className="pxos-btn-primary w-full" onClick={() => setStatus("unknown")}>Continua</button>
          </form>
        )}

        {status === "verified" && (
          <div className="mt-6 text-center space-y-4" data-testid="verify-already-verified">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <div className="text-sm">
              <strong>Il tuo account è già stato verificato.</strong><br />
              Puoi accedere direttamente con la tua password.
            </div>
            <Link to="/login" className="pxos-btn-primary inline-flex" data-testid="verify-goto-login">Vai al login</Link>
          </div>
        )}

        {status === "pending" && (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="verify-form">
            <div className="text-sm opacity-80">Inserisci il codice a 6 cifre ricevuto via email a <strong>{email}</strong>.</div>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest">Codice</span>
              <input value={otp} onChange={(e) => setOtp(e.target.value)}
                     className="pxos-input font-mono text-center text-xl tracking-widest"
                     placeholder="123456" inputMode="numeric" autoFocus autoComplete="one-time-code"
                     data-testid="verify-otp-input" />
            </label>
            <button disabled={busy || otp.trim().replace(/\s|-/g,"").length < 6} className="pxos-btn-primary w-full" data-testid="verify-submit">
              {busy ? <Loader2 className="inline animate-spin" size={16} /> : "Verifica email"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={resend} className="underline opacity-80" data-testid="verify-resend">
                Non l'ho ricevuto — invia di nuovo
              </button>
              <Link to="/login" className="underline opacity-80" data-testid="verify-goto-login-2">Vai al login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
